import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    App,
    Breadcrumb,
    Button,
    Card,
    Empty,
    Form,
    Input,
    Modal,
    Popconfirm,
    Space,
    Spin,
    Switch,
    Table,
    Tabs,
    Tag,
} from 'antd'
import {
    ApartmentOutlined,
    BankOutlined,
    DeleteOutlined,
    EditOutlined,
    EnvironmentOutlined,
    HomeOutlined,
    PlusOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../components/common/FilterBar'
import {
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
} from '../../components/common/PaginatedListTable'
import { PageLayout } from '../../components/layout'
import { usePermissions } from '../../hooks/usePermissions'
import { useSingleFlightAction } from '../../hooks/useSingleFlightAction'
import { getLocalizedApiErrorMessage } from '../../utils/apiErrorI18n'
import { createDedupedMessageApi } from '../../utils/messageDedup'
import { normalizeSearchText } from '../../utils/searchText'
import {
    Region,
    RegionDeviceSummary,
    RegionSimple,
    RegionUserSummary,
    createCustomRegion,
    deleteCustomRegion,
    getRegions,
    getRegion,
    getRegionDevices,
    getRegionPath,
    getRegionUsers,
    searchRegions,
    updateCustomRegion,
} from '../../services/regionService'

type RegionModalMode = 'create' | 'edit'

const REGION_TABLE_SCROLL_X = 920

const createRegionMutationKey = (prefix: string): string => {
    const randomId =
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2)}`

    return `${prefix}-${randomId}`
}

const isFormValidationError = (
    error: unknown
): error is {
    errorFields: unknown[]
} => typeof error === 'object' && error !== null && 'errorFields' in error

const RegionManagement: React.FC = () => {
    const { message: rawMessage } = App.useApp()
    const message = useMemo(
        () => createDedupedMessageApi(rawMessage),
        [rawMessage]
    )
    const { t } = useTranslation('admin')
    const formatApiError = useCallback(
        (error: unknown, fallback: string) =>
            getLocalizedApiErrorMessage(error, fallback, t),
        [t]
    )
    const {
        canCreateRegions,
        canDeleteRegions,
        canReadDevices,
        canReadRegions,
        canReadUsers,
        canUpdateRegions,
    } = usePermissions()

    const [searchLoading, setSearchLoading] = useState(false)
    const [searchKeyword, setSearchKeyword] = useState('')
    const [searchResults, setSearchResults] = useState<RegionSimple[] | null>(
        null
    )
    const [searchCurrentPage, setSearchCurrentPage] = useState(1)
    const [breadcrumb, setBreadcrumb] = useState<RegionSimple[]>([])
    const [currentRegion, setCurrentRegion] = useState<RegionSimple | null>(
        null
    )
    const [currentChildren, setCurrentChildren] = useState<RegionSimple[]>([])
    const [currentUsers, setCurrentUsers] = useState<RegionUserSummary[]>([])
    const [currentDevices, setCurrentDevices] = useState<RegionDeviceSummary[]>(
        []
    )
    const [childrenLoading, setChildrenLoading] = useState(false)
    const [usersLoading, setUsersLoading] = useState(false)
    const [devicesLoading, setDevicesLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('children')
    const [rootCurrentPage, setRootCurrentPage] = useState(1)
    const [rootPageSize, setRootPageSize] = useState(10)
    const [childrenCurrentPage, setChildrenCurrentPage] = useState(1)
    const [childrenPageSize, setChildrenPageSize] = useState(10)
    const [regionModalVisible, setRegionModalVisible] = useState(false)
    const [regionModalMode, setRegionModalMode] =
        useState<RegionModalMode>('create')
    const [editingRegion, setEditingRegion] = useState<Region | null>(null)
    const [regionModalParentName, setRegionModalParentName] = useState('')
    const [savingRegion, setSavingRegion] = useState(false)
    const [openDeleteRegionId, setOpenDeleteRegionId] = useState<number | null>(
        null
    )
    const [form] = Form.useForm()

    useEffect(() => {
        const currentPage = currentRegion
            ? childrenCurrentPage
            : rootCurrentPage
        const pageSize = currentRegion ? childrenPageSize : rootPageSize
        const setCurrentPage = currentRegion
            ? setChildrenCurrentPage
            : setRootCurrentPage
        const maxPage = Math.max(
            1,
            Math.ceil(currentChildren.length / pageSize)
        )
        if (currentPage > maxPage) {
            setCurrentPage(maxPage)
        }
    }, [
        childrenCurrentPage,
        childrenPageSize,
        currentChildren.length,
        currentRegion,
        rootCurrentPage,
        rootPageSize,
    ])

    const getLevelMeta = (level: number) => {
        const levelMap: Record<
            number,
            { label: string; color: string; icon: React.ReactNode }
        > = {
            1: {
                label: t('region.levelLabels.province', {
                    defaultValue: '省/直辖市',
                }),
                color: 'blue',
                icon: <BankOutlined />,
            },
            2: {
                label: t('region.levelLabels.city', { defaultValue: '市' }),
                color: 'green',
                icon: <ApartmentOutlined />,
            },
            3: {
                label: t('region.levelLabels.district', {
                    defaultValue: '区/县',
                }),
                color: 'orange',
                icon: <EnvironmentOutlined />,
            },
            4: {
                label: t('region.levelLabels.street', {
                    defaultValue: '镇/街道',
                }),
                color: 'cyan',
                icon: <HomeOutlined />,
            },
            5: {
                label: t('region.levelLabels.village', {
                    defaultValue: '村/社区',
                }),
                color: 'purple',
                icon: <EnvironmentOutlined />,
            },
            6: {
                label: t('region.levelLabels.custom', {
                    defaultValue: '自定义业务区',
                }),
                color: 'magenta',
                icon: <EnvironmentOutlined />,
            },
        }

        return (
            levelMap[level] || {
                label: t('region.levelLabels.unknown', {
                    defaultValue: `Level ${level}`,
                    level,
                }),
                color: 'default',
                icon: <EnvironmentOutlined />,
            }
        )
    }

    const loadRootRegions = useCallback(async () => {
        if (!canReadRegions) {
            return
        }

        setChildrenLoading(true)
        try {
            const data = await getRegions({
                level: 1,
                is_active: true,
                include_inactive: true,
            })
            setCurrentChildren(data)
            setCurrentRegion(null)
            setBreadcrumb([])
            setCurrentUsers([])
            setCurrentDevices([])
            setActiveTab('children')
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('region.messages.loadFailed'))
            )
        } finally {
            setChildrenLoading(false)
        }
    }, [canReadRegions, formatApiError, message, t])

    useEffect(() => {
        if (canReadRegions) {
            void loadRootRegions()
        }
    }, [canReadRegions, loadRootRegions])

    const loadRegionDetails = useCallback(
        (regionId: number) => {
            if (canReadUsers) {
                setUsersLoading(true)
                getRegionUsers(regionId)
                    .then((users) => setCurrentUsers(users))
                    .catch((error: unknown) => {
                        message.error(
                            formatApiError(
                                error,
                                t('region.messages.loadUsersFailed')
                            )
                        )
                    })
                    .finally(() => setUsersLoading(false))
            } else {
                setCurrentUsers([])
                setUsersLoading(false)
            }

            if (canReadDevices) {
                setDevicesLoading(true)
                getRegionDevices(regionId)
                    .then((devices) => setCurrentDevices(devices))
                    .catch((error: unknown) => {
                        message.error(
                            formatApiError(
                                error,
                                t('region.messages.loadDevicesFailed')
                            )
                        )
                    })
                    .finally(() => setDevicesLoading(false))
            } else {
                setCurrentDevices([])
                setDevicesLoading(false)
            }
        },
        [canReadDevices, canReadUsers, formatApiError, message, t]
    )

    const handleDrillDown = useCallback(
        async (region: RegionSimple) => {
            setChildrenLoading(true)
            try {
                const [children, path] = await Promise.all([
                    getRegions({
                        parent_id: region.id,
                        is_active: true,
                        include_inactive: true,
                    }),
                    getRegionPath(region.id),
                ])
                setCurrentChildren(children)
                setCurrentRegion(region)
                setBreadcrumb(path)
                setActiveTab('children')
                setChildrenCurrentPage(1)
                setSearchResults(null)
                void loadRegionDetails(region.id)
            } catch (error: unknown) {
                message.error(
                    formatApiError(
                        error,
                        t('region.messages.loadChildrenFailed')
                    )
                )
            } finally {
                setChildrenLoading(false)
            }
        },
        [formatApiError, loadRegionDetails, message, t]
    )

    const refreshCurrentView = useCallback(async () => {
        if (!currentRegion) {
            await loadRootRegions()
            return
        }

        try {
            const refreshedRegion = await getRegion(currentRegion.id)
            await handleDrillDown(refreshedRegion)
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('region.messages.loadFailed'))
            )
        }
    }, [
        currentRegion,
        formatApiError,
        handleDrillDown,
        loadRootRegions,
        message,
        t,
    ])

    const navigateAfterDelete = async (region: RegionSimple) => {
        if (currentRegion?.id !== region.id) {
            await refreshCurrentView()
            return
        }

        const parent = breadcrumb[breadcrumb.length - 2]
        if (parent) {
            await handleDrillDown(parent)
        } else {
            await loadRootRegions()
        }
    }

    const handleBreadcrumbClick = async (regionId?: number) => {
        if (!regionId) {
            await loadRootRegions()
            return
        }

        const targetRegion = breadcrumb.find((item) => item.id === regionId)
        if (targetRegion) {
            await handleDrillDown(targetRegion)
        }
    }

    const handleSearch = async (keyword: string) => {
        const trimmedKeyword = normalizeSearchText(keyword)
        setSearchCurrentPage(1)
        if (currentRegion) {
            setChildrenCurrentPage(1)
        } else {
            setRootCurrentPage(1)
        }

        if (!trimmedKeyword) {
            setSearchResults(null)
            await refreshCurrentView()
            return
        }

        setSearchLoading(true)
        try {
            const results = await searchRegions(trimmedKeyword, {
                limit: 50,
                includeInactive: true,
            })
            setSearchResults(results)
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('region.messages.searchFailed'))
            )
        } finally {
            setSearchLoading(false)
        }
    }

    const handleResetSearch = () => {
        setSearchKeyword('')
        setSearchResults(null)
        setSearchCurrentPage(1)
    }

    const closeRegionModal = useCallback(() => {
        setRegionModalVisible(false)
        setEditingRegion(null)
        setRegionModalParentName('')
    }, [])

    const findLoadedRegion = useCallback(
        (regionId: number): RegionSimple | null => {
            const loadedRegions = [
                currentRegion,
                ...breadcrumb,
                ...currentChildren,
                ...(searchResults ?? []),
            ]

            return (
                loadedRegions.find(
                    (region): region is RegionSimple =>
                        region !== null && region.id === regionId
                ) ?? null
            )
        },
        [breadcrumb, currentChildren, currentRegion, searchResults]
    )

    const resolveParentRegionName = useCallback(
        async (parentId: number | null) => {
            if (parentId === null) {
                return t('region.scopeRoot')
            }

            const loadedParent = findLoadedRegion(parentId)
            if (loadedParent) {
                return loadedParent.name
            }

            const parentRegion = await getRegion(parentId)
            return parentRegion.name
        },
        [findLoadedRegion, t]
    )

    const handleOpenCreate = () => {
        if (!currentRegion || currentRegion.is_custom || !canCreateRegions) {
            return
        }

        setRegionModalParentName('')
        setRegionModalMode('create')
        setEditingRegion(null)
        form.resetFields()
        form.setFieldsValue({ is_active: true })
        setRegionModalParentName(currentRegion.name)
        setRegionModalVisible(true)
    }

    const handleOpenEdit = async (region: RegionSimple) => {
        if (!region.is_custom || !canUpdateRegions) {
            return
        }

        try {
            setRegionModalParentName('')
            const detail = await getRegion(region.id)
            const parentName = await resolveParentRegionName(detail.parent_id)
            setRegionModalMode('edit')
            setEditingRegion(detail)
            form.setFieldsValue({
                name: detail.name,
                is_active: detail.is_active,
            })
            setRegionModalParentName(parentName)
            setRegionModalVisible(true)
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('region.messages.loadFailed'))
            )
        }
    }

    const handleDelete = async (region: RegionSimple) => {
        if (!region.is_custom || !canDeleteRegions) {
            return
        }

        try {
            await deleteCustomRegion(region.id)
            message.success(t('region.messages.deleteSuccess'))
            setSearchResults(null)
            await navigateAfterDelete(region)
        } catch (error: unknown) {
            message.error(
                formatApiError(error, t('region.messages.deleteFailed'))
            )
        }
    }

    const submitRegion = useCallback(async () => {
        try {
            const values = await form.validateFields()
            setSavingRegion(true)

            if (regionModalMode === 'create') {
                if (!currentRegion) {
                    return
                }

                await createCustomRegion(
                    {
                        name: values.name,
                        parent_id: currentRegion.id,
                    },
                    {
                        dedupeKey: `region:create:${currentRegion.id}:${values.name}`,
                        idempotencyKey:
                            createRegionMutationKey('region-create'),
                    }
                )
                message.success(t('region.messages.createSuccess'))
                closeRegionModal()
                await refreshCurrentView()
                return
            }

            if (!editingRegion) {
                return
            }

            await updateCustomRegion(
                editingRegion.id,
                {
                    name: values.name,
                    is_active: values.is_active,
                },
                {
                    dedupeKey: `region:update:${editingRegion.id}:${values.name}:${values.is_active}`,
                    idempotencyKey: createRegionMutationKey('region-update'),
                }
            )
            message.success(t('region.messages.updateSuccess'))
            closeRegionModal()
            setSearchResults(null)
            await refreshCurrentView()
        } catch (error: unknown) {
            if (!isFormValidationError(error)) {
                message.error(
                    formatApiError(error, t('region.messages.operationFailed'))
                )
            }
        } finally {
            setSavingRegion(false)
        }
    }, [
        currentRegion,
        editingRegion,
        closeRegionModal,
        form,
        formatApiError,
        message,
        refreshCurrentView,
        regionModalMode,
        t,
    ])
    const { run: handleSubmitRegion, running: submittingRegion } =
        useSingleFlightAction(submitRegion)

    const visibleRegionCurrentPage = currentRegion
        ? childrenCurrentPage
        : rootCurrentPage
    const visibleRegionPageSize = currentRegion
        ? childrenPageSize
        : rootPageSize
    const updateVisibleRegionPagination = (
        page: number,
        nextPageSize?: number
    ) => {
        const pageSize = nextPageSize ?? visibleRegionPageSize
        const pageChangedSize = pageSize !== visibleRegionPageSize

        if (currentRegion) {
            setChildrenPageSize(pageSize)
            setChildrenCurrentPage(pageChangedSize ? 1 : page)
            return
        }

        setRootPageSize(pageSize)
        setRootCurrentPage(pageChangedSize ? 1 : page)
    }

    const renderRegionName = (record: RegionSimple) => {
        const meta = getLevelMeta(record.level)

        return (
            <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => handleDrillDown(record)}
            >
                <Space size={6}>
                    {meta.icon}
                    <span>{record.name}</span>
                    {record.is_custom && (
                        <Tag color="magenta">
                            {t('region.labels.custom', {
                                defaultValue: '自定义区域',
                            })}
                        </Tag>
                    )}
                    {record.is_custom && record.is_active === false && (
                        <Tag color="red">
                            {t('region.labels.inactive', {
                                defaultValue: '已停用',
                            })}
                        </Tag>
                    )}
                </Space>
            </Button>
        )
    }

    const renderRegionActions = (record: RegionSimple) => (
        <Space size="small">
            {record.is_custom && canUpdateRegions && (
                <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => void handleOpenEdit(record)}
                >
                    {t('region.actions.edit')}
                </Button>
            )}
            {record.is_custom && canDeleteRegions && (
                <Popconfirm
                    title={t('region.confirmDelete')}
                    open={openDeleteRegionId === record.id}
                    onOpenChange={(open) => {
                        if (open) {
                            setOpenDeleteRegionId(record.id)
                        }
                    }}
                    onCancel={() => setOpenDeleteRegionId(null)}
                    onConfirm={() => {
                        setOpenDeleteRegionId(null)
                        handleDelete(record)
                    }}
                    okText={t('common:buttons.ok')}
                    cancelText={t('common:buttons.cancel')}
                >
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                    >
                        {t('region.actions.delete')}
                    </Button>
                </Popconfirm>
            )}
        </Space>
    )

    const regionColumns = [
        {
            title: t('region.columns.name'),
            dataIndex: 'name',
            key: 'name',
            width: 300,
            render: (_: string, record: RegionSimple) =>
                renderRegionName(record),
        },
        {
            title: t('region.columns.code'),
            dataIndex: 'code',
            key: 'code',
            width: 150,
        },
        {
            title: t('region.columns.level'),
            dataIndex: 'level',
            key: 'level',
            width: 150,
            render: (level: number) => {
                const meta = getLevelMeta(level)
                return <Tag color={meta.color}>{meta.label}</Tag>
            },
        },
        {
            title: t('region.columns.status', { defaultValue: '状态' }),
            dataIndex: 'is_active',
            key: 'is_active',
            width: 100,
            render: (value: boolean) => (
                <Tag color={value ? 'green' : 'red'}>
                    {value
                        ? t('region.status.active', { defaultValue: '启用' })
                        : t('region.status.inactive', { defaultValue: '停用' })}
                </Tag>
            ),
        },
        {
            title: t('region.columns.actions'),
            key: 'actions',
            width: 220,
            fixed: 'right' as const,
            render: (_: unknown, record: RegionSimple) =>
                renderRegionActions(record),
        },
    ]

    const userColumns = [
        {
            title: t('user.columns.username'),
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: t('user.columns.status'),
            dataIndex: 'is_active',
            key: 'is_active',
            render: (value: boolean) => (
                <Tag color={value ? 'green' : 'red'}>
                    {value
                        ? t('user.status.active')
                        : t('user.status.inactive')}
                </Tag>
            ),
        },
    ]

    const deviceColumns = [
        {
            title: t('region.deviceColumns.name', { defaultValue: '名称' }),
            dataIndex: 'name',
            key: 'name',
            render: (
                value: string | null | undefined,
                record: RegionDeviceSummary
            ) => value || record.address || record.id || '-',
        },
        {
            title: t('region.deviceColumns.status', { defaultValue: '状态' }),
            dataIndex: 'status',
            key: 'status',
            render: (value: number) => (
                <Tag color={value === 1 ? 'green' : 'red'}>
                    {value === 1
                        ? t('region.deviceStatus.online', {
                              defaultValue: '在线',
                          })
                        : t('region.deviceStatus.offline', {
                              defaultValue: '离线',
                          })}
                </Tag>
            ),
        },
    ]

    const tabItems = [
        {
            key: 'children',
            label: t('region.tabs.children', {
                defaultValue: `子区域 (${currentChildren.length})`,
                count: currentChildren.length,
            }),
            children: (
                <Spin spinning={childrenLoading}>
                    {currentChildren.length > 0 ? (
                        <Table
                            columns={regionColumns}
                            dataSource={currentChildren}
                            rowKey="id"
                            size={PAGINATED_LIST_TABLE_SIZE}
                            tableLayout="fixed"
                            scroll={{ x: REGION_TABLE_SCROLL_X }}
                            pagination={buildPaginatedListPagination({
                                current: visibleRegionCurrentPage,
                                pageSize: visibleRegionPageSize,
                                showTotal: (total: number) =>
                                    t('region.totalRegions', { total }),
                                onChange: updateVisibleRegionPagination,
                            })}
                        />
                    ) : (
                        <Empty
                            description={t('region.empty.children', {
                                defaultValue: '此区域下无子区域',
                            })}
                        />
                    )}
                </Spin>
            ),
        },
    ]

    if (currentRegion && canReadUsers) {
        tabItems.push({
            key: 'users',
            label: t('region.tabs.users', {
                defaultValue: `相关用户 (${currentUsers.length})`,
                count: currentUsers.length,
            }),
            children: (
                <Spin spinning={usersLoading}>
                    <Table
                        columns={userColumns}
                        dataSource={currentUsers}
                        rowKey="id"
                        size={PAGINATED_LIST_TABLE_SIZE}
                        pagination={buildPaginatedListPagination({
                            pageSize: 10,
                        })}
                    />
                </Spin>
            ),
        })
    }

    if (currentRegion && canReadDevices) {
        tabItems.push({
            key: 'devices',
            label: t('region.tabs.devices', {
                defaultValue: `相关设备 (${currentDevices.length})`,
                count: currentDevices.length,
            }),
            children: (
                <Spin spinning={devicesLoading}>
                    <Table
                        columns={deviceColumns}
                        dataSource={currentDevices}
                        rowKey="id"
                        size={PAGINATED_LIST_TABLE_SIZE}
                        pagination={buildPaginatedListPagination({
                            pageSize: 10,
                        })}
                    />
                </Spin>
            ),
        })
    }

    return (
        <PageLayout
            title={t('region.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.systemManagement') },
                { title: t('navigation:breadcrumb.regionManagement') },
            ]}
        >
            <Card>
                <div
                    style={{
                        marginBottom: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        flexWrap: 'wrap',
                    }}
                >
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <Button
                                type="link"
                                style={{ padding: 0, height: 'auto' }}
                                onClick={() => void handleBreadcrumbClick()}
                            >
                                {t('region.scopeRoot', {
                                    defaultValue: '可管理区域',
                                })}
                            </Button>
                        </Breadcrumb.Item>
                        {breadcrumb.map((item, index) => (
                            <Breadcrumb.Item key={item.id}>
                                {index < breadcrumb.length - 1 ? (
                                    <Button
                                        type="link"
                                        style={{ padding: 0, height: 'auto' }}
                                        onClick={() =>
                                            void handleBreadcrumbClick(item.id)
                                        }
                                    >
                                        {item.name}
                                    </Button>
                                ) : (
                                    <Space size={6}>
                                        <span>{item.name}</span>
                                        {item.is_custom && (
                                            <Tag color="magenta">
                                                {t('region.labels.custom', {
                                                    defaultValue: '自定义区域',
                                                })}
                                            </Tag>
                                        )}
                                    </Space>
                                )}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>

                    <Space wrap>
                        {currentRegion?.is_custom && canUpdateRegions && (
                            <Button
                                icon={<EditOutlined />}
                                onClick={() =>
                                    void handleOpenEdit(currentRegion)
                                }
                            >
                                {t('region.actions.editCurrent', {
                                    defaultValue: '编辑当前区域',
                                })}
                            </Button>
                        )}
                        {currentRegion?.is_custom && canDeleteRegions && (
                            <Popconfirm
                                title={t('region.confirmDelete')}
                                open={openDeleteRegionId === currentRegion.id}
                                onOpenChange={(open) => {
                                    if (open) {
                                        setOpenDeleteRegionId(currentRegion.id)
                                    }
                                }}
                                onCancel={() => setOpenDeleteRegionId(null)}
                                onConfirm={() => {
                                    setOpenDeleteRegionId(null)
                                    handleDelete(currentRegion)
                                }}
                                okText={t('common:buttons.ok')}
                                cancelText={t('common:buttons.cancel')}
                            >
                                <Button danger icon={<DeleteOutlined />}>
                                    {t('region.actions.deleteCurrent', {
                                        defaultValue: '删除当前区域',
                                    })}
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                </div>

                <FilterBar>
                    <FilterField
                        label={t('region.searchPlaceholder', {
                            defaultValue: '搜索当前可见区域',
                        })}
                    >
                        <Input
                            placeholder={t('region.searchPlaceholder', {
                                defaultValue: '搜索当前可见区域',
                            })}
                            allowClear
                            aria-label="region-search-input"
                            value={searchKeyword}
                            onChange={(event) => {
                                setSearchKeyword(event.target.value)
                                if (!event.target.value) {
                                    setSearchResults(null)
                                }
                            }}
                            onPressEnter={() => handleSearch(searchKeyword)}
                        />
                    </FilterField>
                    <FilterActions>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(searchKeyword)}
                            loading={searchLoading}
                        >
                            {t('common:buttons.search', {
                                defaultValue: '查询',
                            })}
                        </Button>
                        <Button onClick={handleResetSearch}>
                            {t('common:buttons.reset')}
                        </Button>
                    </FilterActions>
                </FilterBar>

                <PaginatedListContainer>
                    {canCreateRegions && (
                        <PaginatedListToolbar data-testid="region-list-toolbar">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleOpenCreate}
                                disabled={
                                    !currentRegion || currentRegion.is_custom
                                }
                            >
                                {t('common:buttons.add', {
                                    defaultValue: '添加',
                                })}
                            </Button>
                        </PaginatedListToolbar>
                    )}

                    <div className="paginated-list-content">
                        {currentRegion && (
                            <div style={{ marginBottom: 12 }}>
                                <Space wrap>
                                    <Tag
                                        color={
                                            getLevelMeta(currentRegion.level)
                                                .color
                                        }
                                    >
                                        {getLevelMeta(currentRegion.level).label}
                                    </Tag>
                                    {currentRegion.is_custom && (
                                        <Tag color="magenta">
                                            {t('region.labels.custom', {
                                                defaultValue: '自定义区域',
                                            })}
                                        </Tag>
                                    )}
                                    {currentRegion.is_custom &&
                                        currentRegion.is_active === false && (
                                            <Tag color="red">
                                                {t('region.labels.inactive', {
                                                    defaultValue: '已停用',
                                                })}
                                            </Tag>
                                        )}
                                    {!currentRegion.is_custom && (
                                        <span style={{ color: '#666' }}>
                                            {t('region.createHint', {
                                                defaultValue:
                                                    '可在当前标准区域下创建自定义叶子区域。',
                                            })}
                                        </span>
                                    )}
                                </Space>
                            </div>
                        )}

                        {searchResults !== null ? (
                            <div>
                                <div style={{ marginBottom: 8, color: '#666' }}>
                                    {t('region.searchResults', {
                                        defaultValue: `搜索到 ${searchResults.length} 个结果`,
                                        count: searchResults.length,
                                    })}
                                </div>
                                <Table
                                    columns={regionColumns}
                                    dataSource={searchResults}
                                    rowKey="id"
                                    size={PAGINATED_LIST_TABLE_SIZE}
                                    tableLayout="fixed"
                                    scroll={{ x: REGION_TABLE_SCROLL_X }}
                                    pagination={buildPaginatedListPagination({
                                        current: searchCurrentPage,
                                        pageSize: 10,
                                        onChange: (page) =>
                                            setSearchCurrentPage(page),
                                    })}
                                />
                            </div>
                        ) : (
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={tabItems}
                            />
                        )}
                    </div>
                </PaginatedListContainer>
            </Card>

            <Modal
                title={
                    regionModalMode === 'create'
                        ? t('region.modal.createCustomTitle', {
                              defaultValue: '创建自定义区域',
                          })
                        : t('region.modal.editCustomTitle', {
                              defaultValue: '编辑自定义区域',
                          })
                }
                open={regionModalVisible}
                onOk={handleSubmitRegion}
                onCancel={closeRegionModal}
                okText={t('common:buttons.save')}
                cancelText={t('common:buttons.cancel')}
                confirmLoading={savingRegion || submittingRegion}
                maskClosable={false}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t('region.form.name')}
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: t('region.form.nameRequired'),
                            },
                            {
                                max: 50,
                                message: t('region.form.nameMaxLength'),
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label={t('region.form.parent')}>
                        <Input
                            value={regionModalParentName}
                            disabled
                        />
                    </Form.Item>
                    {regionModalMode === 'edit' && (
                        <Form.Item
                            label={t('region.form.status')}
                            name="is_active"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </PageLayout>
    )
}

export default RegionManagement
